<template>
  <div class="orderBox">
    <div class="orderBox-background">
      <div class="containerBox">
        <a-spin :spinning="spinningState"
                wrapper-class-name="containerBox-spin">
          <div v-for="(item, index) in addressList"
               :key="index"
               class="containerBox-body "
               :class="item.id == shadowIndex ? boxClass : ''">
            <div class="containerBox-body-box"
                 :class="props.isCart ? '' : 'box-border'"
                 @click="addressCellClick(item)">
              <div class="containerBox-body-box-left">
                <img :src="addressIcon"
                     class="address-icon" />
                <div>
                  <div style="display: flex;align-items: center;">
                    <span class="address-title">{{ item.name }}</span>
                    <span class="address-title">{{ item.phone }}</span>
                    <span v-if="item.default"
                          class="address-default">{{ $t('orderInfo.AddressesList.defaultAddress') }}</span>
                  </div>
                  <div>{{ item.province.name }}{{ item.city.name }}{{ item.district.name }}{{ item.street.name }}{{
                    item.address
                  }}</div>
                </div>
              </div>
              <div class="containerBox-body-box-right">
                <div v-if="!item.default"
                     class="containerBox-body-box-right-defaultButton"
                     @click="editDefault(item)">{{ $t('orderInfo.AddressesList.setAsDefault') }}</div>
                <div class="containerBox-body-box-right-useButton"
                     @click="editAddress(item)">{{ $t('orderInfo.AddressesList.edit') }}</div>
                <div class="containerBox-body-box-right-delButton"
                     @click="deleteAddress(item)">{{ $t('orderInfo.AddressesList.delete') }}</div>
              </div>
            </div>
          </div>
        </a-spin>
        <div v-if="!props.isCart"
             class="containerBox-footer">
          <a-button @click="addAddress">
            <template #icon>
              <plus-square-filled />
            </template>
            {{ $t('orderInfo.AddressesList.newAddress') }}
          </a-button>
        </div>
      </div>
    </div>
  </div>
  <AddressModal v-if="modalState"
                :state="modalState"
                :address="address"
                :mode="modalMode"
                @refresh="initialization"
                @modalController="modalController" />
</template>

<script setup lang="ts">
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { message, Modal } from 'ant-design-vue'
import { createVNode, onMounted, ref, watch } from 'vue'
import { addressDelete, addressGetAll, addressUpdate } from '../api/address'
import type { AddressModel } from '../api/interface/AddressInterface'
import addressIcon from '../assets/ic_address.png'
import AddressModal from '../components/AddressModel.vue'
import { useI18n } from "vue-i18n"
const { t } = useI18n()
const modalState = ref<boolean>(false)
const spinningState = ref<boolean>(false)
const addressList = ref<AddressModel[]>([])
const address = ref<AddressModel>({
  id: 0,
  name: '',
  phone: '',
  province: {
    code: '',
    name: ''
  },
  city: {
    code: '',
    name: ''
  },
  default: false,
  district: {
    code: '',
    name: ''
  },
  street: {
    code: '',
    name: ''
  },
  address: ''
})
const props = defineProps<{
  isCart: boolean
  isAdd: boolean
  selectId: number
}>()
const modalMode = ref<'edit' | 'create'>('create')
const emits = defineEmits(['closeAddModal'])
onMounted(() => {
  initialization()
  props.selectId ? shadowIndex.value = props.selectId : ''
  boxClass.value = props.isCart ? 'address-shadow' : ''
})

// declared Methods
const initialization = (): void => {
  spinningState.value = !spinningState.value
  addressGetAll().then(result => {
    const { addresses } = result
    addressList.value = [...addresses]
    spinningState.value = !spinningState.value
    const addArr = addressList.value.filter(address => address.id == shadowIndex.value)
    address.value = addArr[0]
  })
}
const boxClass = ref<string>('')
const shadowIndex = ref<number>()
const addressCellClick = (data: AddressModel) => {
  address.value = data
  boxClass.value = props.isCart ? 'address-shadow' : ''
  shadowIndex.value = data.id
}
watch(() => props.isAdd, () => {
  props.isAdd ? addAddress() : ''
},
  { deep: true })
const addAddress = (): void => {
  modalMode.value = 'create'
  modalController()
}

const editAddress = (data: AddressModel): void => {
  modalMode.value = 'edit'
  address.value = data
  modalController()
}
const editDefault = (data: AddressModel): void => {
  const editData = {
    id: data.id,
    default: true
  } as any
  Modal.confirm({
    title: t('orderInfo.AddressesList.setAsDefault'),
    icon: createVNode(ExclamationCircleOutlined),
    content: createVNode('div', { style: 'color:red;' }, t('orderInfo.AddressesList.confirmSet')),
    onOk() {
      addressUpdate(editData).then(() => {
        message.success(t('orderInfo.AddressesList.setSuccess'))
        initialization()
      })
    },
  })
}
const deleteAddress = (data: AddressModel): void => {
  Modal.confirm({
    title: t('orderInfo.AddressesList.delete'),
    icon: createVNode(ExclamationCircleOutlined),
    content: createVNode('div', { style: 'color:red;' }, t('orderInfo.AddressesList.confirmDelete')),
    onOk() {
      spinningState.value = true
      addressDelete(data).then(() => {
        message.success(t('orderInfo.AddressesList.deleteSuccessful'))
        spinningState.value = false
        initialization()
      })
    },
  })
}

const modalController = (): void => {
  modalState.value = !modalState.value
  !modalState.value ? emits('closeAddModal') : ''
}

defineExpose({
  address
})
</script>

<style lang="less" scoped>
.orderBox::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari and Opera */
}

.orderBox {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: auto;
  /* 隐藏滚动条 */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */

  .address-shadow {
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
    z-index: 2;
  }

  &-background {
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;

    .containerBox {
      text-align: left;
      width: 100%;
      height: 73vh;

      &-title {
        font-size: 16px;
        font-family: PingFangSC-Medium, PingFang SC;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.8500);
        line-height: 24px;
      }

      &-spin {
        height: 100%;
        overflow: hidden;
        overflow-y: auto;
      }

      :deep(.ant-spin-container) {
        margin: 24px 0;
      }

      &-body {
        overflow: hidden;
        overflow-y: auto;
        z-index: 1;
        margin: 0 24px;

        &:hover {
          background-color: #dad2d236;
        }

        &-box {
          display: flex;
          justify-content: space-between;
          height: 100px;
          cursor: pointer;

          &-left {
            display: flex;
            align-items: center;

            .address-icon {
              width: 24px;
              height: 29px;
              margin: 0 20px;
            }

            .address-title {
              font-size: 18px;
              font-family: 'Microsoft YaHei Bold';
              margin-right: 22px;
            }

            .address-default {
              display: inline-flex;
              justify-content: center;
              width: 54px;
              height: 18px;
              background: #EC9231;
              font-size: 12px;
              color: #FFFFFF;
            }
          }

          &-right {
            display: flex;
            // flex-flow: row wrap;
            // flex-direction: column;
            align-content: center;
            justify-content: center;
            height: 100%;

            div {
              text-align: center;
              font-size: 16px;
            }

            &-defaultButton {
              width: 100px;
              height: 100px;
              line-height: 100px;
              color: #999999;
              cursor: pointer;
            }

            &-useButton {
              width: 100px;
              height: 100px;
              line-height: 100px;
              color: #999999;
              cursor: pointer;
            }

            &-useButton:hover {
              background: #F1F1F1;
            }

            &-delButton {
              width: 100px;
              height: 100px;
              line-height: 100px;
              color: #DE4A09;
              cursor: pointer;
            }

            &-delButton:hover {
              background: rgba(222, 74, 9, .15);
            }
          }
        }

        .box-border {
          border-bottom: 1px solid #E8E8E8;
        }

      }

      &-footer {
        display: flex;
        justify-content: center;
        padding-top: 25px;

        button {
          width: 120px;
          height: 40px;
          background: #111111;
          color: #ffffff;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
